import React from "react"
import { useMutation } from "react-query"
import { GrFormClose } from "react-icons/gr"
import { Box, Flex, IconButton, Tag as ChakraTag, Text, Input, Center, Spinner } from "@chakra-ui/react"

import { OrgEntity } from "../lib/types/org"
import { API_URL } from "../lib/config"
import { fetcher } from "../lib/fetcher"
import { useToast } from "../lib/hooks/useToast"
import { CustomTagsList } from "./CustomTagsList"
import { tagColors } from "../lib/static/tagColors"
import { TagEntity, TagGroupEntity, TagInstance, AssignedTagDeclaration } from "../lib/types/customTag"
import { useGetCustomTagGroupsQuery, useGetCustomTagsQuery } from "../lib/hooks/queries/useGetCustomTags"
import { useOrgs } from "./providers/OrgProvider"
import { BiPlus } from "react-icons/bi"
import { isSupervisor } from "../lib/helpers/role"
import { useMe } from "./providers/MeProvider"

interface CreateTagRequest {
  name: string
  description?: string
  group: string
  organization: string
}

interface CreateTagInstanceRequest {
  declaration: string
  target: string
}

interface Props {
  resourceUrl: string
  assignedTags: AssignedTagDeclaration[]
  refetchResourceTags: () => void
}

export const CustomTags: React.FC<Props> = ({ resourceUrl, assignedTags, refetchResourceTags }) => {
  const toast = useToast()
  const { currentOrgs } = useOrgs()
  const me = useMe()
  const ref = React.useRef<HTMLDivElement>(null)
  const [isTagListActive, setIsTagListActive] = React.useState(false)
  const [tagName, setTagName] = React.useState("")
  const [tagColor, setTagColor] = React.useState("")
  const [tagLoadingId, setTagLoadingId] = React.useState("")
  const [selectedGroup, setSelectedGroup] = React.useState<TagGroupEntity>()
  const [selectedOrg, setSelectedOrg] = React.useState<OrgEntity>()
  const [isCreatingTag, setIsCreatingTag] = React.useState(false)

  const { tagGroups, refetchTagGroups } = useGetCustomTagGroupsQuery()
  const { tags, refetchTags } = useGetCustomTagsQuery()

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [])

  React.useEffect(() => {
    if (isSupervisor(me.roles) && tagName && (tags.length === 0 || !tags.find((t) => t.name === tagName))) {
      setIsCreatingTag(true)
    } else {
      setIsCreatingTag(false)
    }
  }, [tags, tagName, me.roles])

  const randomTagColor = React.useCallback(() => {
    // TODO change this to get last in list once Alex reverses order of tags
    const latestTagColor = assignedTags.length > 0 && assignedTags[0].tag_declaration_color
    const colors = latestTagColor ? tagColors.filter((tc) => tc.color !== latestTagColor) : tagColors
    let index = Math.round(Math.random() * colors.length) - 1
    if (index < 0) index = 0
    return colors[index].color
  }, [assignedTags])

  React.useEffect(() => {
    if (isCreatingTag) {
      setTagColor(randomTagColor())
    }
  }, [isCreatingTag, randomTagColor])

  const { mutateAsync: createTag, isLoading: isCreateTagLoading } = useMutation<
    TagEntity,
    unknown,
    CreateTagRequest
  >((data) => fetcher("custom-tagging/tag_declarations/", { body: JSON.stringify(data), method: "post" }))
  const createNewTag = async (tag: CreateTagRequest) => {
    try {
      const res = await createTag(tag)
      if (res?.uuid) {
        refetchTagGroups()
        refetchTags()
        handleAssignTag(res?.uuid)
      } else {
        toast({ title: "Error creating tag", status: "error" })
      }
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const { mutateAsync: createTagInstance, isLoading: isCreateTagInstanceLoading } = useMutation<
    TagInstance,
    unknown,
    CreateTagInstanceRequest
  >((data) => fetcher("custom-tagging/tag_instances/", { body: JSON.stringify(data), method: "post" }))

  const assignTag = async (tagInstance: CreateTagInstanceRequest) => {
    try {
      const res = await createTagInstance(tagInstance)
      if (res?.uuid) {
        refetchResourceTags()
        refetchTagGroups()
      } else {
        toast({ title: "Error while tagging", status: "error" })
      }
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  React.useEffect(() => {
    if (!isCreatingTag) {
      setSelectedGroup(undefined)
      setSelectedOrg(undefined)
    }
  }, [isCreatingTag])

  const { mutateAsync: removeTagInstance, isLoading: isRemoveTagInstanceLoading } = useMutation<
    string,
    undefined,
    unknown
  >((uuid) => fetcher(`custom-tagging/tag_instances/${uuid}/`, { method: "delete" }))

  const unAssignTag = async (uuid: string) => {
    try {
      const res = await removeTagInstance(uuid)
      if (!res) {
        refetchResourceTags()
        refetchTagGroups()
      } else {
        toast({ title: "Error while untagging", status: "error" })
      }
    } catch {
      toast({ title: "Network error", description: "Please check your internet connection", status: "error" })
    }
  }

  const handleClick = (e: any) => {
    if (ref?.current?.contains(e.target)) return
    setIsTagListActive(false)
    setTagName("")
  }

  const handleCreate = (name: string, color: string) => {
    if (!name.trim()) return
    if (assignedTags.find((t) => t.tag_declaration_name === name)) return
    if (selectedGroup) {
      let org
      if (currentOrgs.length === 1) {
        org = currentOrgs[0]
      } else {
        org = selectedOrg
      }
      if (org) {
        const groupId = selectedGroup.uuid
        const tag = {
          name: name.trim(),
          group: `${API_URL}custom-tagging/tag_groups/${groupId}/`,
          organization: org.url,
          color,
        }
        createNewTag(tag)
      } else {
        toast({ title: "Select an organization", status: "warning" })
      }
    } else {
      toast({ title: "Select a tag group", status: "warning" })
    }
  }

  const handleRemoveTag = (uuid: string) => {
    setTagLoadingId(uuid)
    unAssignTag(uuid)
  }

  const handleAssignTag = (uuid: string) => {
    if (assignedTags.find((t) => t.tag_declaration_name === tagName)) return
    assignTag({
      declaration: `${API_URL}custom-tagging/tag_declarations/${uuid}/`,
      target: resourceUrl,
    })
    setTagName("")
  }

  const isLoading = isCreateTagLoading || isCreateTagInstanceLoading || isRemoveTagInstanceLoading

  return (
    <Box w="100%" h="100%" minH="44px" mb={8} pos="relative">
      <Box
        ref={ref}
        bg="white"
        zIndex={4}
        boxShadow={isTagListActive ? "lg" : undefined}
        borderRadius={4}
        border={isTagListActive ? "1px solid" : undefined}
        borderColor="gray.200"
        _hover={{ bg: isTagListActive ? undefined : "gray.50" }}
        onClick={() => setIsTagListActive(true)}
        overflow="hidden"
      >
        <Flex
          p={1}
          cursor={isTagListActive ? undefined : "pointer"}
          bg={isTagListActive ? "gray.50" : undefined}
          borderBottom={isTagListActive ? "1px solid" : undefined}
          borderColor="gray.100"
          flexWrap="wrap"
          align="center"
        >
          {!isTagListActive && assignedTags.length === 0 && (
            <>
              <Text pl={1} color="grayg.400" fontSize="0.9rem">
                Add tags
              </Text>
              <Box ml={2} color="gray.600" as={BiPlus} />
            </>
          )}

          {assignedTags?.map((tag) => (
            <ChakraTag key={tag.uuid} bg={tag.tag_declaration_color} m={1}>
              {tag.tag_declaration_name}
              {isTagListActive && (
                <IconButton
                  aria-label="Remove tag"
                  icon={
                    isRemoveTagInstanceLoading && tagLoadingId === tag.uuid ? (
                      <Spinner size="xs" mr={1} />
                    ) : (
                      <GrFormClose />
                    )
                  }
                  variant="ghost"
                  h="10px"
                  minW={0}
                  ml={1}
                  _hover={{ bg: "none" }}
                  _active={{ bg: "none" }}
                  onClick={() => handleRemoveTag(tag.uuid)}
                />
              )}
            </ChakraTag>
          ))}
          {isTagListActive && (
            <Box w="auto" minW="60px" flex="1 1 60px">
              <Flex
                as="form"
                h="33px"
                display="flex"
                align="center"
                onSubmit={(e) => {
                  e.preventDefault()
                  const uuid = tags.find((t) => t.name === tagName)?.uuid
                  if (isCreatingTag) {
                    handleCreate(tagName, tagColor)
                  } else if (uuid) {
                    handleAssignTag(uuid)
                  }
                }}
              >
                {isCreateTagInstanceLoading && (
                  <Center h={200} px={2}>
                    <Spinner size="xs" />
                  </Center>
                )}
                {!isLoading && (
                  <Input
                    variant="unstyled"
                    h="100%"
                    p={0}
                    ml={2}
                    border="none"
                    fontSize="0.9rem"
                    value={tagName}
                    autoFocus
                    placeholder={assignedTags.length === 0 ? "Search for an option..." : undefined}
                    onChange={(e) => setTagName(e.target.value)}
                  />
                )}
              </Flex>
            </Box>
          )}
        </Flex>

        {isTagListActive && (
          <CustomTagsList
            {...{
              assignedTags,
              isCreatingTag,
              tagName,
              tagColor,
              tagGroups,
              tags,
              selectedGroup,
              selectedOrg,
            }}
            createTagInstanceIsLoading={isCreateTagInstanceLoading}
            onTagAssign={handleAssignTag}
            onTagCreate={handleCreate}
            onTagDelete={() => {
              refetchTagGroups()
              refetchTags()
              refetchResourceTags()
            }}
            onTagUpdate={() => {
              refetchTagGroups()
              refetchTags()
              refetchResourceTags()
            }}
            onOrgSelected={setSelectedOrg}
            onGroupSelected={setSelectedGroup}
          />
        )}
      </Box>
    </Box>
  )
}
